import { Trans } from '@lingui/macro';
import { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import RoomContent from './RoomContent';
import RoomSearchResults from './RoomSearchResults';
import RoomFavorites from './RoomFavorites';
import RoomHeader from './RoomHeader';
import { RightIframe, RoomRightMenu } from './right-menu/RoomRightMenu';
import { RoomFilesModal, } from "./right-menu/RoomFilesModal";
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { useResponsive } from '../../hooks/useResponsive';
import { Notification } from '../../models/Notification';
import { useMessageContext } from '../main/NotificationModal';

const RoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string; }>();
  const history = useHistory<{ fromNotification?: boolean; notification?: Notification }>();
  const { setNotification } = useMessageContext();

  // Initialize room from cache if available to avoid flickering
  const [room, setRoom] = useState<Room | undefined>(() => My.Rooms.get(roomId || ""));
  // If we have the room, we are not initially loading. If not, we are.
  const [loading, setLoading] = useState<boolean>(!My.Rooms.get(roomId || ""));
  const [isRightMenuOpen, setIsRightMenuOpen] = useState(false);
  const { isMobileOrTablet } = useResponsive();

  const { iframeItem, filter, showFilesModal, setShowFilesModal, enterType, roomsLoaded } = useAppUIContext();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [inProgress, setInProgress] = useState(false);

  // 검색 모드 상태
  const [isSearchMode, setIsSearchMode] = useState(false); // 검색 입력창 표시 여부
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false); // 실제 검색 결과 표시 여부

  // 즐겨찾기 모드 상태
  const [isFavoritesMode, setIsFavoritesMode] = useState(false);

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      setIsSearchActive(true);
    }
  };

  // useEffect로도 감지 (useIonViewDidEnter가 작동하지 않을 경우 대비)
  useEffect(() => {
    if (history.location.state?.fromNotification) {
      const notif = history.location.state?.notification;

      if (notif?.roomId && notif.roomId === roomId) {
        setNotification(notif);
      }

      setIsSearchMode(false);
      setSearchQuery("");
      setIsSearchActive(false);
      setIsFavoritesMode(false);
      // 플래그 제거
      history.replace(history.location.pathname, {});
    }
  }, [history.location.state?.fromNotification, roomId, setNotification]);

  // roomId가 바뀔 때만 파일 모달 닫기 + 검색 모드 종료
  useEffect(() => {
    setShowFilesModal(false);
    setIsSearchMode(false);
    setSearchQuery("");
    setIsSearchActive(false);
    setIsFavoritesMode(false);

    // reset state for new room
    const cached = My.Rooms.get(roomId || "");
    setRoom(cached);
    setLoading(!cached);
  }, [roomId]);

  useEffect(() => {
    LocalStorage.set("last-visited-room", roomId || "")

    let isActive = true;

    const handleRoomUpdate = () => {
      if (!isActive) return;

      const updatedRoom = My.Rooms.get(roomId || "");
      if (updatedRoom) {
        setRoom(updatedRoom);
        setLoading(false);
      }
    };

    const unsubscribe = My.subscribeToRoomUpdates(handleRoomUpdate);
    unsubscribeRef.current = unsubscribe;

    const cachedRoom = My.Rooms.get(roomId || "");
    if (cachedRoom) {
      setRoom(cachedRoom);
      setLoading(false);
    } else if (roomsLoaded) {
      setLoading(true);
      Fire.getRoom(roomId || "").then((r: Room | undefined) => {
        if (isActive) {
          if (r && r.userIds?.includes(My.user.id)) {
            setRoom(r);
          } else {
            setRoom(undefined);
          }
          setLoading(false);
        }
      }).catch((e) => {
        console.error('[RoomPage] Fire.getRoom error', e);
        if (isActive) setLoading(false);
      });
    } else {
      // Still waiting for LeftMenuRooms to populate initial room list
      setLoading(true);
    }

    return () => {
      isActive = false;
      unsubscribe();
      if (unsubscribeRef.current === unsubscribe) {
        unsubscribeRef.current = null;
      }
    };
  }, [roomId, roomsLoaded]);

  if (loading) {
    return (
      <div className='h-full flex flex-col'>
        <div className="h-14 border-b flex items-center px-4"></div>
        <div className="flex-1 flex items-start justify-center pt-20">
          <div className="w-64 h-1 bg-gray-100 mt-8 rounded overflow-hidden">
            <div className="h-full bg-primary animate-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (room) {
    return (
      <div className="h-full flex flex-row gap-4">
        {/* Main content area - 별도의 rounded panel */}
        <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden bg-white md:rounded-panel md:border md:border-[#E5E5E5]">
          {/* 헤더는 항상 표시 */}
          <RoomHeader
            room={room}
            type={filter}
            inProgress={inProgress}
            isSearchMode={isSearchMode}
            searchQuery={searchQuery}
            isFavoritesMode={isFavoritesMode}
            onSearchModeChange={(mode) => {
              setIsSearchMode(mode);
              if (!mode) {
                setSearchQuery("");
                setIsSearchActive(false);
              }
            }}
            onFavoritesModeChange={setIsFavoritesMode}
            onSearchQueryChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            onRightMenuToggle={() => setIsRightMenuOpen(true)}
          />

          {/* 검색 결과, 즐겨찾기, 또는 일반 컨텐츠 */}
          <div className="flex-1 flex flex-col min-h-0 relative">
            {isSearchActive && searchQuery.trim() ? (
              <RoomSearchResults
                room={room}
                searchQuery={searchQuery}
              />
            ) : isFavoritesMode ? (
              <RoomFavorites room={room} />
            ) : (
              <RoomContent key={room.id} room={room} onProgressChange={setInProgress} />
            )}
          </div>
        </div>

        {/* Right sidebar - Desktop only (별도의 rounded panel) */}
        {!isMobileOrTablet && (
          iframeItem ?
            <RightIframe item={iframeItem} />
            :
            <RoomRightMenu type={"none"} room={room} />
        )}

        {/* Right sidebar - Mobile Sheet */}
        {isMobileOrTablet && (
          <Sheet open={isRightMenuOpen} onOpenChange={setIsRightMenuOpen}>
            <SheetContent side="right" className="w-[304px] p-0">
              {iframeItem ?
                <RightIframe item={iframeItem} />
                :
                <RoomRightMenu
                  type={"none"}
                  room={room}
                  onClose={() => setIsRightMenuOpen(false)}

                />
              }
            </SheetContent>
          </Sheet>
        )}

        {/* Modals */}
        <RoomFilesModal room={room} enterType={enterType} isFilesModalOpen={showFilesModal} closeHandler={() => setShowFilesModal(false)} />
      </div>
    );
  } else {
    return (
      <div className='h-full flex flex-col'>
        <div className="h-14 border-b flex items-center px-4"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className='flex flex-col items-center gap-4 text-center'>
            <img src="assets/img/img-cover-question-160.png" alt="" className="w-40 h-40" />
            <h1 className="text-2xl font-bold text-gray-900"><Trans>Room not found</Trans></h1>
            <p className="text-gray-600"><Trans>Room may not exist or you may not be a member</Trans></p>
          </div>
        </div>
      </div>
    );
  }
};

export default RoomPage;
